import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Gem,
  Shield,
  Banknote,
  FileCheck,
  Settings,
  Plus,
  Trash2,
  Edit,
  Star,
  Check,
  X,
  Loader2,
  RefreshCw,
  Globe,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminJewelleryServices = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [selectedCountry, setSelectedCountry] = useState('SL');
  const [settings, setSettings] = useState(null);
  const [allSettings, setAllSettings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const countries = [
    { code: 'SL', name: 'Sri Lanka', currency: 'Rs' },
    { code: 'UK', name: 'United Kingdom', currency: '£' },
    { code: 'US', name: 'United States', currency: '$' },
    { code: 'IN', name: 'India', currency: '₹' },
    { code: 'AE', name: 'UAE', currency: 'AED' },
  ];

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, allSettingsRes, providersRes] = await Promise.all([
        axios.get(`${API_URL}/api/jewellery-services/admin/settings/${selectedCountry}`),
        axios.get(`${API_URL}/api/jewellery-services/admin/settings`),
        axios.get(`${API_URL}/api/jewellery-services/admin/providers?country=${selectedCountry}`),
      ]);
      setSettings(settingsRes.data);
      setAllSettings(allSettingsRes.data.settings || []);
      setProviders(providersRes.data.providers || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/jewellery-services/admin/settings/${selectedCountry}`, settings);
      alert('Settings saved successfully!');
    } catch (err) {
      console.error('Failed to save settings:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleProvider = async (providerId) => {
    try {
      await axios.put(`${API_URL}/api/jewellery-services/admin/providers/${providerId}/toggle`);
      fetchData();
    } catch (err) {
      console.error('Failed to toggle provider:', err);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Gem className="h-6 w-6 text-yellow-500" />
            Jewellery Services Management
          </h2>
          <p className="text-gray-500">Manage insurance, loans, and valuation services</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-48">
              <Globe className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {countries.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <Button
          variant={activeTab === 'settings' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('settings')}
          className={activeTab === 'settings' ? 'bg-yellow-500' : ''}
        >
          <Settings className="h-4 w-4 mr-2" />
          Country Settings
        </Button>
        <Button
          variant={activeTab === 'insurance' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('insurance')}
          className={activeTab === 'insurance' ? 'bg-yellow-500' : ''}
        >
          <Shield className="h-4 w-4 mr-2" />
          Insurance Providers
        </Button>
        <Button
          variant={activeTab === 'loans' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('loans')}
          className={activeTab === 'loans' ? 'bg-yellow-500' : ''}
        >
          <Banknote className="h-4 w-4 mr-2" />
          Loan Providers
        </Button>
        <Button
          variant={activeTab === 'valuation' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('valuation')}
          className={activeTab === 'valuation' ? 'bg-yellow-500' : ''}
        >
          <FileCheck className="h-4 w-4 mr-2" />
          Valuation Services
        </Button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && settings && (
        <div className="space-y-6">
          {/* Service Toggles */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Service Availability - {selectedCountry}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Jewellery Insurance</Label>
                  <p className="text-sm text-gray-500">Allow users to get insurance quotes for jewellery</p>
                </div>
                <Switch
                  checked={settings.insurance_enabled}
                  onCheckedChange={(v) => updateSetting('insurance_enabled', v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Gold & Jewellery Loans</Label>
                  <p className="text-sm text-gray-500">Allow users to apply for gold loans and pawning</p>
                </div>
                <Switch
                  checked={settings.loans_enabled}
                  onCheckedChange={(v) => updateSetting('loans_enabled', v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Valuation Services</Label>
                  <p className="text-sm text-gray-500">Show certified valuation providers</p>
                </div>
                <Switch
                  checked={settings.valuation_enabled}
                  onCheckedChange={(v) => updateSetting('valuation_enabled', v)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Configuration</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum Insurance Value</Label>
                <Input
                  type="number"
                  value={settings.min_insurance_value || 10000}
                  onChange={(e) => updateSetting('min_insurance_value', Number(e.target.value))}
                />
                <p className="text-xs text-gray-500">Minimum item value to show insurance option</p>
              </div>
              <div className="space-y-2">
                <Label>Maximum Loan-to-Value (%)</Label>
                <Input
                  type="number"
                  value={settings.max_loan_ltv || 75}
                  onChange={(e) => updateSetting('max_loan_ltv', Number(e.target.value))}
                />
                <p className="text-xs text-gray-500">Maximum LTV ratio for loans</p>
              </div>
            </CardContent>
          </Card>

          {/* Country Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">All Countries Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>Insurance</TableHead>
                    <TableHead>Loans</TableHead>
                    <TableHead>Valuation</TableHead>
                    <TableHead>Min Insurance Value</TableHead>
                    <TableHead>Max LTV</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allSettings.map((s) => (
                    <TableRow key={s.country}>
                      <TableCell className="font-medium">{s.country}</TableCell>
                      <TableCell>
                        {s.insurance_enabled ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <X className="h-5 w-5 text-red-500" />
                        )}
                      </TableCell>
                      <TableCell>
                        {s.loans_enabled ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <X className="h-5 w-5 text-red-500" />
                        )}
                      </TableCell>
                      <TableCell>
                        {s.valuation_enabled !== false ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <X className="h-5 w-5 text-red-500" />
                        )}
                      </TableCell>
                      <TableCell>{s.min_insurance_value?.toLocaleString() || '10,000'}</TableCell>
                      <TableCell>{s.max_loan_ltv || 75}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Button onClick={saveSettings} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save Settings for {selectedCountry}
          </Button>
        </div>
      )}

      {/* Insurance Providers Tab */}
      {activeTab === 'insurance' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-500" />
              Insurance Providers - {selectedCountry}
            </CardTitle>
            <Button size="sm" className="bg-blue-500">
              <Plus className="h-4 w-4 mr-1" />
              Add Provider
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Premium Rate</TableHead>
                  <TableHead>Coverage</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers
                  .filter(p => p.service_type === 'jewellery-insurance')
                  .map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{provider.provider_name}</p>
                          <p className="text-sm text-gray-500">{provider.business_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>{provider.premium_rate}% p.a.</TableCell>
                      <TableCell>
                        {provider.min_coverage_value?.toLocaleString()} - {provider.max_coverage_value?.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          {provider.rating?.toFixed(1)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={provider.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {provider.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleProvider(provider.id)}>
                            {provider.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button size="sm" variant="outline">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {providers.filter(p => p.service_type === 'jewellery-insurance').length === 0 && (
              <p className="text-center py-8 text-gray-500">No insurance providers for {selectedCountry}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loan Providers Tab */}
      {activeTab === 'loans' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote className="h-5 w-5 text-green-500" />
              Loan Providers - {selectedCountry}
            </CardTitle>
            <Button size="sm" className="bg-green-500">
              <Plus className="h-4 w-4 mr-1" />
              Add Provider
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Interest Rate</TableHead>
                  <TableHead>Max LTV</TableHead>
                  <TableHead>Loan Range</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers
                  .filter(p => p.service_type === 'jewellery-loans')
                  .map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{provider.provider_name}</p>
                          <p className="text-sm text-gray-500">{provider.business_name}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-green-600">
                        {provider.interest_rate}% p.a.
                      </TableCell>
                      <TableCell>{provider.max_ltv}%</TableCell>
                      <TableCell>
                        {provider.min_loan_amount?.toLocaleString()} - {provider.max_loan_amount?.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          {provider.rating?.toFixed(1)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={provider.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {provider.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleProvider(provider.id)}>
                            {provider.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button size="sm" variant="outline">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {providers.filter(p => p.service_type === 'jewellery-loans').length === 0 && (
              <p className="text-center py-8 text-gray-500">No loan providers for {selectedCountry}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Valuation Services Tab */}
      {activeTab === 'valuation' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-purple-500" />
              Valuation Services - {selectedCountry}
            </CardTitle>
            <Button size="sm" className="bg-purple-500">
              <Plus className="h-4 w-4 mr-1" />
              Add Provider
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Services</TableHead>
                  <TableHead>Certifications</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers
                  .filter(p => p.service_type === 'jewellery-valuation')
                  .map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{provider.provider_name}</p>
                          <p className="text-sm text-gray-500">{provider.business_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {provider.services?.slice(0, 2).map((s, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {provider.certifications?.join(', ')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          {provider.rating?.toFixed(1)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={provider.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {provider.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleProvider(provider.id)}>
                            {provider.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button size="sm" variant="outline">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {providers.filter(p => p.service_type === 'jewellery-valuation').length === 0 && (
              <p className="text-center py-8 text-gray-500">No valuation providers for {selectedCountry}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminJewelleryServices;
